import { useEffect, useState } from "react";
import { Share2, Download, Copy, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fetchBlob = async (url) => (await api.get(url, { responseType: "blob", baseURL: "" })).data;

export default function WelcomeKit() {
  const [kit, setKit] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/customer/kit").then((r) => setKit(r.data)).catch(() => {}); }, []);
  if (!kit) return null;

  const URLS = { status: kit.status_url, card: kit.card_url, "ride-status": kit.ride_status_url, "ride-card": kit.ride_card_url };
  const copyCaption = async () => { await navigator.clipboard.writeText(kit.caption); toast.success("Caption copied — paste it with your image"); };

  const download = async (kind) => {
    setBusy(kind);
    try {
      const blob = await fetchBlob(URLS[kind]);
      const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: `random-rider-${kind}-${kit.referral_code}.jpg` });
      a.click();
      URL.revokeObjectURL(a.href);
    } finally { setBusy(""); }
  };

  const share = async (kind) => {
    setBusy(kind);
    try {
      const blob = await fetchBlob(URLS[kind]);
      const file = new File([blob], `random-rider-${kind}.jpg`, { type: "image/jpeg" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: kit.caption });
        toast.success("Shared — thank you for spreading the word!");
      } else {
        await navigator.clipboard.writeText(kit.caption);
        const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: file.name });
        a.click();
        toast.success("Image downloaded & caption copied — post them on WhatsApp");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Could not share — try Download instead");
    } finally { setBusy(""); }
  };

  const Tile = ({ kind, label, ratio, src }) => (
    <div className="space-y-3" data-testid={`kit-${kind}`}>
      <div className={`rounded-2xl overflow-hidden border border-ink/10 bg-ink ${ratio}`}>
        <img src={src} alt={label} className="w-full h-full object-cover" data-testid={`kit-${kind}-preview`} />
      </div>
      <p className="text-xs font-semibold text-ink/70">{label}</p>
      <div className="flex gap-2">
        <button onClick={() => share(kind)} disabled={!!busy} data-testid={`kit-${kind}-share`} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#25D366] text-white text-xs font-semibold py-2.5 rounded-full hover:brightness-110 disabled:opacity-50"><Share2 size={13} /> Share to WhatsApp</button>
        <button onClick={() => download(kind)} disabled={!!busy} aria-label="Download" data-testid={`kit-${kind}-download`} className="inline-flex items-center justify-center border border-ink/15 text-ink px-3 rounded-full hover:bg-sand disabled:opacity-50"><Download size={14} /></button>
      </div>
    </div>
  );

  return (
    <div className="bg-white border border-ink/10 rounded-3xl p-6" data-testid="welcome-kit">
      <h3 className="font-serif text-xl font-bold flex items-center gap-2"><Sparkles size={18} className="text-gold" /> Your welcome kit</h3>
      <p className="text-xs text-ink/60 mt-1">Ready-made visuals with your link and QR code. Post the tall one as a WhatsApp Status, send the wide one in chats.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
        <Tile kind="status" label="WhatsApp Status · Golden Beach · 1080×1920" ratio="aspect-[9/16]" src={kit.status_url} />
        <Tile kind="card" label="Chat card · Golden Beach · 1200×630" ratio="aspect-[1200/630]" src={kit.card_url} />
        <Tile kind="ride-status" label="WhatsApp Status · Marine Drive ride · 1080×1920" ratio="aspect-[9/16]" src={kit.ride_status_url} />
        <Tile kind="ride-card" label="Chat card · Marine Drive ride · 1200×630" ratio="aspect-[1200/630]" src={kit.ride_card_url} />
      </div>
      <div className="mt-5 bg-sand rounded-2xl p-4">
        <p className="text-[11px] font-mono uppercase tracking-widest text-ink/50">Caption</p>
        <p className="text-sm text-ink/80 mt-1 whitespace-pre-line break-words" data-testid="kit-caption">{kit.caption}</p>
        <button onClick={copyCaption} data-testid="kit-caption-copy" className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-ink border border-ink/15 px-3.5 py-2 rounded-full hover:bg-white"><Copy size={12} /> Copy caption</button>
      </div>
    </div>
  );
}
